
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, BarChart3, ShieldCheck, Award, TrendingUp, Users, FileText } from 'lucide-react';
import { useData } from '../context/DataContext';
import Counter from '../components/Counter';
import FAQ from '../components/FAQ';

const Home: React.FC = () => {
   const { trucks, settings, openQuoteModal } = useData();
   // Filter only featured trucks.
   const featuredTrucks = trucks
      .filter(t => t.featured)
      .sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));

   const brands = ["SHACMAN"];

   return (
      <div className="bg-white min-h-screen font-sans">

         {/* 1. Hero Section - Kept Dark for Premium Feel, but cleaner text */}
         <section className="relative h-screen w-full overflow-hidden">
            <div className="absolute inset-0 w-full h-full bg-black">
               <div className="absolute inset-0 bg-[url('/images/hero-bg-2.jpg')] bg-cover bg-center opacity-30"></div>
               <iframe
                  className="absolute top-1/2 left-1/2 w-[400%] h-[400%] md:w-[150%] md:h-[150%] -translate-x-1/2 -translate-y-1/2 opacity-80 pointer-events-none"
                  src={`https://www.youtube.com/embed/${settings.heroVideoUrl}?si=Tg3MuyTTffUqRvYZ&autoplay=1&mute=1&controls=0&loop=1&playlist=${settings.heroVideoUrl}&showinfo=0&rel=0&iv_load_policy=3&disablekb=1&modestbranding=1`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="YouTube video player"
               ></iframe>
               {/* Modern Gradient Overlay */}
               <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent pointer-events-none"></div>
            </div>

            <div className="relative h-full flex flex-col justify-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto z-10">
               <div className="space-y-8 max-w-3xl">
                  <h1 className="font-display text-6xl md:text-7xl lg:text-8xl font-bold text-white uppercase leading-none tracking-tight drop-shadow-xl">
                     Power Your <br />
                     <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">Business</span>
                  </h1>

                  <p className="max-w-xl text-gray-300 text-lg md:text-xl font-light leading-relaxed border-l-4 border-automax-red pl-6">
                     {settings.heroSubheadline}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 pt-4">
                     <Link
                        to="/inventory"
                        className="group relative px-8 py-4 bg-automax-red text-white font-display font-bold text-xl uppercase tracking-wider overflow-hidden rounded-sm w-full sm:w-auto text-center"
                     >
                        <div className="absolute inset-0 w-full h-full bg-red-700 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                        <span className="relative flex items-center justify-center gap-2">View Inventory <ArrowRight size={20} /></span>
                     </Link>
                     <button
                        type="button"
                        onClick={openQuoteModal}
                        className="px-8 py-4 border border-white text-white hover:bg-white hover:text-black font-display font-bold text-xl uppercase tracking-wider rounded-sm transition-all w-full sm:w-auto flex items-center justify-center gap-2 cursor-pointer"
                     >
                        <FileText size={20} /> Get Free Quote
                     </button>
                  </div>
               </div>
            </div>
         </section>

         {/* 2. Brand Trust Strip - Professional White Section */}
         <section className="bg-white border-b border-gray-100 py-10 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4">
               <p className="text-center text-gray-400 text-xs font-bold uppercase tracking-[0.2em] mb-6">Trusted Brands We Stock</p>
               <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
                  {brands.map((brand, i) => (
                     <h3 key={i} className="text-2xl md:text-3xl font-display font-bold text-gray-800 tracking-wider hover:text-automax-red cursor-default transition-colors">{brand}</h3>
                  ))}
               </div>
            </div>
         </section>

         {/* 3. Featured Inventory - Clean Light Background */}
         <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div
                  className="flex flex-col md:flex-row justify-between items-end mb-16"
               >
                  <div>
                     <h4 className="text-automax-red font-bold uppercase tracking-wider text-sm mb-2">Premium Selection</h4>
                     <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 uppercase">Featured Inventory</h2>
                  </div>
                  <Link to="/inventory" className="hidden md:flex items-center text-gray-900 hover:text-automax-red transition-colors uppercase font-bold tracking-wider text-sm border-b-2 border-gray-200 hover:border-automax-red pb-1">
                     View All 150+ Trucks <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {featuredTrucks.map((truck, index) => (
                     <div
                        key={truck.id}
                        className="group bg-white rounded-sm shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 flex flex-col h-full"
                     >
                        <div className="relative aspect-[16/10] overflow-hidden bg-gray-200">
                           <img src={truck.image} alt={truck.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                           <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-900 rounded-sm">
                              {truck.condition}
                           </div>
                           {/* Overlay Action */}
                           <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <Link to={`/inventory/${truck.id}`} className="bg-white text-gray-900 px-6 py-3 font-bold uppercase text-sm rounded-sm hover:bg-automax-red hover:text-white transition-colors transform translate-y-4 group-hover:translate-y-0 duration-300">
                                 View Details
                              </Link>
                           </div>
                        </div>
                        <div className="p-8 flex flex-col flex-grow">
                           <div className="flex justify-between items-start mb-4">
                              <div>
                                 <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">{truck.brand}</div>
                                 <h3 className="font-display text-2xl font-bold text-gray-900 leading-tight group-hover:text-automax-red transition-colors line-clamp-1">{truck.name}</h3>
                              </div>
                           </div>
                           <div className="text-right mb-4">
                              {/* Price removed */}
                           </div>

                           <div className="mt-auto flex items-center justify-between text-sm text-gray-500 pt-6 border-t border-gray-100">
                              {truck.category?.toLowerCase().includes('trailer') ? (
                                 <span className="font-bold text-gray-900">{truck.category}</span>
                              ) : (
                                 <span className="flex items-center gap-2"><TrendingUp size={16} /> {truck.maxSpeed}</span>
                              )}
                              <span className="font-bold text-gray-900">{truck.location}</span>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>

               <div className="mt-12 text-center md:hidden">
                  <Link to="/inventory" className="inline-block bg-gray-900 text-white px-8 py-4 uppercase font-bold tracking-wider text-sm rounded-sm">
                     View All Inventory
                  </Link>
               </div>
            </div>
         </section>

         {/* 4. Statistics - Clean Strip */}
         <section className="bg-automax-dark py-16 border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4">
               <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-gray-800">
                  <div className="p-4">
                     <div className="font-display text-5xl font-bold text-white mb-2"><Counter value={15} suffix="+" /></div>
                     <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">Years Experience</div>
                  </div>
                  <div className="p-4">
                     <div className="font-display text-5xl font-bold text-white mb-2"><Counter value={5000} suffix="+" disableFormatting /></div>
                     <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">Vehicles Delivered</div>
                  </div>
                  <div className="p-4">
                     <div className="font-display text-5xl font-bold text-white mb-2"><Counter value={98} suffix="%" /></div>
                     <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">Client Satisfaction</div>
                  </div>
                  <div className="p-4">
                     <div className="font-display text-5xl font-bold text-white mb-2"><Counter value={5} /></div>
                     <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">Regional Offices</div>
                  </div>
               </div>
            </div>
         </section>

         {/* 5. Why Choose Us - White & Corporate */}
         <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="text-center max-w-3xl mx-auto mb-20">
                  <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 uppercase mb-4">The Automax Standard</h2>
                  <p className="text-gray-600 text-lg">From selecting right vehicles to accessing the best financing solutions.</p>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  <div
                     className="bg-gray-50 p-10 rounded-sm text-center hover:shadow-lg transition-all border border-gray-100 hover:translate-y-[-10px] duration-300"
                  >
                     <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm text-automax-red">
                        <ShieldCheck size={32} />
                     </div>
                     <h3 className="font-display text-2xl font-bold text-gray-900 uppercase mb-4">Certified Quality</h3>
                     <p className="text-gray-600 leading-relaxed">Every vehicle undergoes a rigorous 150-point mechanical inspection by certified diesel mechanics before listing.</p>
                  </div>

                  <div
                     className="bg-gray-50 p-10 rounded-sm text-center hover:shadow-lg transition-all border border-gray-100 hover:translate-y-[-10px] duration-300"
                  >
                     <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm text-automax-red">
                        <BarChart3 size={32} />
                     </div>
                     <h3 className="font-display text-2xl font-bold text-gray-900 uppercase mb-4">Flexible Financing</h3>
                     <p className="text-gray-600 leading-relaxed">We offer tailored lease-to-own and loan packages with competitive interest rates for East African businesses.</p>
                  </div>

                  <div
                     className="bg-gray-50 p-10 rounded-sm text-center hover:shadow-lg transition-all border border-gray-100 hover:translate-y-[-10px] duration-300"
                  >
                     <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm text-automax-red">
                        <Users size={32} />
                     </div>
                     <h3 className="font-display text-2xl font-bold text-gray-900 uppercase mb-4">Regional Support</h3>
                     <p className="text-gray-600 leading-relaxed">With offices in Dar es Salaam, Zambia, and Kampala, our support network ensures you never stop moving.</p>
                  </div>
               </div>
            </div>
         </section>



         {/* 6. Promotion Section (Replaced Testimonials) */}
         <section className="py-24 bg-neutral-900 relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#333 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-automax-red/10 to-transparent pointer-events-none"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
               <div className="flex flex-col lg:flex-row items-center gap-16">

                  {/* Left: Text Content */}
                  <div
                     className="flex-1 text-left"
                  >
                     <div className="inline-flex items-center gap-2 bg-automax-red/20 text-automax-red border border-automax-red/30 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-8">
                        <span className="w-2 h-2 rounded-full bg-automax-red animate-pulse"></span>
                        {settings.promoTag}
                     </div>
                     <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-none mb-6">
                        {settings.promoTitle}
                     </h2>
                     <p className="text-gray-400 text-lg mb-8 leading-relaxed max-w-xl">
                        {settings.promoSubtitle}
                     </p>

                     <ul className="space-y-5 mb-10">
                        {(settings.promoBenefits || []).map((item, i) => (
                           <li
                              key={i}
                              className="flex items-center text-white font-medium text-lg"
                           >
                              <div className="bg-gradient-to-br from-automax-red to-red-800 p-1 rounded-full mr-4 shadow-lg shadow-red-900/50">
                                 <CheckCircle size={16} className="text-white" />
                              </div>
                              {item}
                           </li>
                        ))}
                     </ul>

                     <div className="flex flex-col sm:flex-row gap-4">
                        <Link to="/inventory" className="bg-white text-neutral-900 px-8 py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-gray-200 transition-colors flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-300">
                           View Eligible Trucks
                        </Link>
                        <button onClick={openQuoteModal} className="border-2 border-white/20 text-white px-8 py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-white/10 transition-colors flex items-center justify-center">
                           Claim Offer
                        </button>
                     </div>
                  </div>

                  {/* Right: Visual */}
                  <div
                     className="flex-1 relative w-full"
                  >
                     {/* Decorative Elements */}
                     <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-gradient-to-tr from-automax-red/20 to-transparent rounded-full blur-3xl opacity-60"></div>

                     {/* Truck Image Container with float animation */}
                     <div
                        className="relative z-10"
                     >
                        <img
                           src={settings.promoImage}
                           alt="Premium Truck Promotion"
                           className="rounded-sm shadow-2xl border-4 border-white/5 w-full object-cover transform rotate-2 hover:rotate-0 transition-all duration-700 hover:scale-105"
                           style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
                        />

                        {/* Floating Badge */}
                        <div
                           className="absolute -bottom-8 -left-8 md:-left-12 bg-white text-neutral-900 p-6 rounded-sm shadow-2xl w-40 h-40 flex flex-col items-center justify-center border-b-8 border-automax-red z-20 transform -rotate-6 hover:rotate-6 transition-transform duration-500"
                        >
                           <span className="text-5xl font-display font-bold text-automax-red">{settings.promoDiscountValue}</span>
                           <span className="text-xs font-bold uppercase text-center leading-tight tracking-wider mt-1">{settings.promoDiscountLabel}</span>
                        </div>
                     </div>
                  </div>

               </div>
            </div>
         </section>

         {/* 7. FAQ Section */}
         <div id="faq">
            <FAQ items={settings.homeFaqs} />
         </div>

         {/* 8. Call to Action Band */}
         <section className="bg-automax-red py-20 relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
               <h2 className="font-display text-4xl md:text-5xl font-bold text-white uppercase mb-6">Ready to Expand Your Fleet?</h2>
               <p className="text-white/90 text-xl max-w-2xl mx-auto mb-10">Get pre-approved for financing or schedule a viewing of our latest stock today.</p>
               <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <button type="button" onClick={openQuoteModal} className="bg-white text-automax-red px-10 py-4 font-bold text-lg uppercase rounded-sm hover:bg-gray-100 transition-colors shadow-lg cursor-pointer">
                     Request Free Quote
                  </button>
                  <Link to="/financing" className="border-2 border-white text-white px-10 py-4 font-bold text-lg uppercase rounded-sm hover:bg-white hover:text-automax-red transition-colors">
                     Calculate Payments
                  </Link>
               </div>
            </div>
         </section>

      </div>
   );
};

export default Home;
